/**
 * Teacher Review Service for fetching student conversations under teacher review
 */

import { createAbortSafeErrorHandler, defaultErrorHandler as commonErrorHandler, createHeaders, getApiUrls } from './serviceConfig';

export type TeacherReviewMessageDto = {
  attachments: Array<{
    contentType: string;
    createdAt: string;
    fileName: string;
    sizeInBytes: number;
    url: string;
  }>;
  content: string;
  createdAt: string;
  id: string;
  isFlagged: boolean;
  participant: 'user' | 'assistant' | 'system';
};

export type TeacherReviewConversation = {
  createdAt: string;
  id: string;
  messages: TeacherReviewMessageDto[];
  mode: string;
  status: 'ACTIVE' | 'ARCHIVED';
  title: string;
  updatedAt: string;
};

type TeacherReviewServiceConfig = {
  onError?: (error: unknown) => void;
};

type TeacherReviewService = {
  getConversation: (conversationId: string) => Promise<TeacherReviewConversation>;
};

/**
 * Creates a configured teacher review service
 */
export function createTeacherReviewService(config: TeacherReviewServiceConfig = {}): TeacherReviewService {
  const configuredErrorHandler = config.onError || ((error: unknown) => commonErrorHandler('Teacher review service', error as Error));

  const defaultErrorHandler = createAbortSafeErrorHandler(configuredErrorHandler) as (error: unknown) => void;

  const getConversation = async (conversationId: string): Promise<TeacherReviewConversation> => {
    if (!conversationId) {
      throw new Error('Conversation ID is required');
    }

    try {
      const urls = getApiUrls();
      const response = await fetch(urls.teacherReview.conversation(conversationId), {
        method: 'GET',
        headers: createHeaders(),
      });

      if (!response.ok) {
        throw new Error(`Failed to get student conversation: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      defaultErrorHandler(error);
      throw error;
    }
  };

  return { getConversation };
}

export default createTeacherReviewService();
